// allowance.component.ts
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, ProcessCellForExportParams } from 'ag-grid-community';
import { AllowanceRow } from '../../models/ratings.type';
import { ServerGridCore } from '../../models/BaseServerGrid';
import { RatingsService } from '../../shared/services/ratings/ratings.service';
import { compareDates, dateFilterComparator, formatDMY } from '../../shared/utils/date-helpers';
import { UserService } from '../../shared/services/user.service';
import { Router } from '@angular/router';
import { BookmarksComponent } from "../bookmarks/bookmarks.component";
import { MatSidenavModule } from "@angular/material/sidenav";
import { AppraisalDataService } from "../../shared/services/appraisal-data.service";

@Component({
  selector: 'app-allowance',
  standalone: true,
  imports: [CommonModule, AgGridModule, MatSidenavModule, BookmarksComponent],
  templateUrl: './allowance.component.html',
  styleUrls: ['./allowance.component.scss']
})
export class AllowanceComponent extends ServerGridCore<AllowanceRow> {
  bookmarkGrid: string = "allowance";
  override api!: GridApi;
  override columnDefs: ColDef[] = [
    { field: 'companyName', headerName: 'Company', filter: 'agTextColumnFilter', hide: true, minWidth: 200 },
    { field: 'clientReference', headerName: 'Client Ref', filter: 'agTextColumnFilter' },
    { field: 'addressPrimary', headerName: 'Address', filter: 'agTextColumnFilter', minWidth: 220 },
    { field: 'cityName', headerName: 'City', filter: 'agTextColumnFilter' },
    { field: 'countryName', headerName: 'Country', filter: 'agTextColumnFilter' },
    { field: 'allowanceType', headerName: 'Allowance Type', filter: 'agTextColumnFilter' },
    {
      field: 'liabilityStartDate',
      headerName: 'Liability Start',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    {
      field: 'liabilityEndDate', 
      headerName: 'Liability End',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    {
      field: 'allowanceStartDate', 
      headerName: 'Allowance Start',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    {
      field: 'allowanceEndDate', 
      headerName: 'Allowance End',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    { field: 'reliefPercent', headerName: 'Relief %', filter: 'agNumberColumnFilter' },
    { field: 'allowanceYear2023', headerName: '23/24', filter: 'agNumberColumnFilter' },
    { field: 'allowanceYear2024', headerName: '24/25', filter: 'agNumberColumnFilter' },
    { field: 'allowanceYear2025', headerName: '25/26', filter: 'agNumberColumnFilter' },
    { field: 'allowanceYear2026', headerName: '26/27', filter: 'agNumberColumnFilter' },
    { field: 'revaluationYear', headerName: 'Revaluation Period', hide: true,  filter: 'agTextColumnFilter' }
  ];

  userData: any;
  constructor(private apiSvc: RatingsService, private userService: UserService, public router: Router,
    private appraisalDataService: AppraisalDataService
  ) { super(); }

  async ngOnInit() {
    this.userService.userData$.subscribe((data) => {
      this.userData = data;
      if (!this.userData.roles.includes('Ratings')) {
        this.router.navigate(['/404']);
      }
    });
  }

  onGridReady(e: GridReadyEvent) {
    this.api = e.api;
    this.api.setGridOption('serverSideDatasource', this.makeDatasource());
  }

  excelStyles = [
    { id: 'header', font: { bold: true } },
    { id: 'percent', numberFormat: { format: '0.00%' } },
    { id: 'number', numberFormat: { format: '#,##0.00' } },
    { id: 'int', numberFormat: { format: '#,##0' } },
    { id: 'date', dataType: 'dateTime', numberFormat: { format: 'dd-mm-yyyy' } }
  ];

  onExport() {
    if (!this.api) return;

    const today = new Date();
    const fileName = `allowances_${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}.xlsx`;

    const hasExcel = typeof (this.api as any).exportDataAsExcel === 'function';

    if (hasExcel) {
      // Excel (Enterprise)
      (this.api as any).exportDataAsExcel({
        fileName,
        sheetName: 'Allowances',
        // Export only the columns currently shown & in the shown order:
        columnKeys: this.api.getAllDisplayedColumns().map(c => c.getColId()),
        // Keep only filtered rows; set allColumns:true/exportAllRows:true if you want otherwise.
        exportMode: 'xlsx',
        processCellCallback: (p: ProcessCellForExportParams) => {
          // Normalize dates to yyyy-mm-dd in the file:
          const field = p.column.getColDef().field;
          const dateFields = new Set([
            'liabilityStartDate', 'liabilityEndDate', 'allowanceStartDate', 'allowanceEndDate'
          ]);
          if (field && dateFields.has(field) && p.value) {
            const d = new Date(p.value);
            if (!isNaN(d.getTime())) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          }
          return p.value;
        },
        excelStyles: this.excelStyles
      });
    } else {
      // CSV (Community fallback)
      this.api.exportDataAsCsv({
        fileName: fileName.replace('.xlsx', '.csv'),
        columnKeys: this.api.getAllDisplayedColumns().map(c => c.getColId()),
        processCellCallback: p => {
          const field = p.column.getColDef().field;
          const dateFields = new Set([
            'liabilityStartDate', 'liabilityEndDate', 'allowanceStartDate', 'allowanceEndDate'
          ]);
          if (field && dateFields.has(field) && p.value) {
            const d = new Date(p.value);
            if (!isNaN(d.getTime())) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          }
          return p.value;
        }
      });
    }
  }


  override project(): string[] {
    const m: Record<string, string> = {
      id: 'ID', clientReference: 'ClientReference', addressPrimary: 'AddressPrimary', cityName: 'CityName', countryName: 'CountryName',
      liabilityStartDate: 'LiabilityStartDate', liabilityEndDate: 'LiabilityEndDate', allowanceStatus: 'AllowanceStatus', allowanceType: 'AllowanceType',
      allowanceStartDate: 'AllowanceStartDate', allowanceEndDate: 'AllowanceEndDate', reliefPercent: 'ReliefPercent', allowanceID: 'AllowanceID',
      revaluationYear: 'RevaluationYear', companyName: 'CompanyName', allowanceYear2023: 'AllowanceYear2023',
      allowanceYear2024: 'AllowanceYear2024', allowanceYear2025: 'AllowanceYear2025', allowanceYear2026: 'AllowanceYear2026'
    };
    const cols = this.api?.getAllDisplayedColumns() || [];
    return cols.map(c => m[c.getColDef().field!] || c.getColDef().field!);
  }

  override query(body: any) { return this.apiSvc.allowanceQuery(body); }

  isBookmarksPanelOpen: boolean = false;
  ontoggleBookmarkSidenav() {
    let state = this.api.getState();
    this.appraisalDataService.setGridStateData(state);
    this.isBookmarksPanelOpen = !this.isBookmarksPanelOpen;
  }
  onStateUpdated(): void {
    let state = this.api.getState();
    this.appraisalDataService.setGridStateData(state);
  }

  loadBookmark() {
    let state = this.appraisalDataService.getGridStateData();
    this.api.setState(state);
  }
}
